import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { BellIcon } from "@heroicons/react/24/outline";
import i18n from "../utils/i18n/i18n";
import { STRING_ACCESS_TOKEN_CHECKLIST, STRING_LANGUAGE, STRING_SIDEBAR_EXPANDED } from "../utils/const/string-const";

const languages = [
    { value: "en", label: "English", flag: "🇺🇸" },
    { value: "es", label: "Español", flag: "🇪🇸" },
    { value: "pt", label: "Português", flag: "🇧🇷" },
];

export default function LayoutHeader({ userInfo }) {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("es");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const menuRef = useRef(null);
    const languageDropdownRef = useRef(null);
    const history = useHistory();

    // Sync language from localStorage on mount
    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE);
        if (language) {
            setSelectedLanguage(language);
            i18n.changeLanguage(language);
        }
    }, []);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        if (showUserMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showUserMenu]);

    // Close language dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
                setIsLanguageDropdownOpen(false);
            }
        };

        if (isLanguageDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isLanguageDropdownOpen]);

    const handleLogout = () => {
        // console.log('Logging out from dashboard');
        localStorage.removeItem(STRING_LANGUAGE);
        localStorage.removeItem(STRING_ACCESS_TOKEN_CHECKLIST);
        localStorage.removeItem(STRING_SIDEBAR_EXPANDED);
        setShowUserMenu(false);
        history.push(`/login`);
    };

    const handleGoToProfile = () => {
        setShowUserMenu(false);
        // history.push(`/profile`);
    };

    const getUserInitials = () => {
        if (!userInfo || !userInfo.nickname) return "U";
        return userInfo.nickname.substring(0, 2).toUpperCase();
    };

    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
            <div className="flex items-center justify-between px-4 lg:px-6 h-16">
                {/* Left - Logo (visible on mobile when sidebar is hidden) */}
                <div className="flex items-center gap-4">
                    {/* <div className="lg:hidden flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">UM</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">Ultima Milla</span>
                    </div> */}
                    {/* <h1 className="hidden lg:block text-xl font-semibold text-gray-900">Dashboard</h1> */}
                </div>

                {/* Right - Notifications, Language & User Menu */}
                <div className="flex items-center gap-3">

                    {/* Language Dropdown */}
                    <div className="relative" ref={languageDropdownRef}>
                        <button
                            type="button"
                            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                            className="flex items-center gap-2 px-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <span className="text-lg">
                                {languages.find((lang) => lang.value === selectedLanguage)?.flag}
                            </span>
                            <span className="text-sm font-medium hidden sm:inline">
                                {languages.find((lang) => lang.value === selectedLanguage)?.label}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isLanguageDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]">
                                {languages.map((language) => (
                                    <button
                                        type="button"
                                        key={language.value}
                                        onClick={() => {
                                            setSelectedLanguage(language.value);
                                            setIsLanguageDropdownOpen(false);
                                            i18n.changeLanguage(language.value);
                                            localStorage.setItem(STRING_LANGUAGE, language.value);
                                        }}
                                        className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 transition ${selectedLanguage === language.value ? "bg-blue-50 text-blue-600" : "text-gray-700"}`}
                                    >
                                        <span className="text-lg">{language.flag}</span>
                                        <span className="font-medium text-sm">{language.label}</span>
                                        {selectedLanguage === language.value && (
                                            <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Notifications */}
                    <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <BellIcon className="h-6 w-6" />
                        <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                    </button>

                    {/* User Menu */}
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            {/* Avatar */}
                            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                    {getUserInitials()}
                                </span>
                            </div>
                            {/* User Name (hidden on small screens) */}
                            <div className="hidden sm:block text-left">
                                <p className="text-sm font-semibold text-gray-900">
                                    {userInfo?.nombreEmpresa}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {userInfo?.nombreUsuario}
                                </p>
                            </div>
                            {/* Chevron */}
                            <svg
                                className={`w-4 h-4 text-gray-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {showUserMenu && (
                            <div
                                className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999] animate-fade-in"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* User Info Header */}
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <p className="text-sm font-semibold text-gray-900">
                                        {userInfo?.nickname || "Usuario"}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {userInfo?.email || "admin@comprartodo.com"}
                                    </p>
                                </div>

                                {/* Menu Options */}
                                <div className="py-1">
                                    <button
                                        onClick={handleGoToProfile}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <span className="font-medium">Mi Perfil</span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            history.push('/configuracion');
                                        }}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="font-medium">Configuración</span>
                                    </button>

                                    <div className="border-t border-gray-100 my-1"></div>

                                    <button
                                        onClick={handleLogout}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                        <span className="font-medium">Cerrar Sesión</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}

